import {
  ALERT_DELIVERY_STATUS,
  EVENT_SEVERITY,
  formatParisDateTime,
  type EventListItem,
  type EventView,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { ListToolbar } from '@/components/data-table/list-toolbar.js';
import { OriginBadge } from '@/components/filters/origin-badge.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import { RunsTable } from '@/features/runs/runs-table.js';
import { ApiError } from '@/lib/api/client.js';
import { useOriginFilter } from '@/lib/origin-filter.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchJournalList } from './api.js';
import { JournalDetailCell } from './journal-detail-cell.js';
import { hasActiveJournalFilters, JournalFilters, readJournalFilters } from './journal-filters.js';

/**
 * Colonnes triables — uniquement celles honorées par `sortBy` côté serveur
 * (`apps/server/src/http/api/events/list.ts::SORT_FIELDS`).
 */
const SORTABLE_COLUMNS = ['at', 'severity', 'code', 'origin'] as const;
const PAGE_SIZE = 50;

/** Les trois onglets de `02` §11 : le quotidien, les problèmes, les runs. */
type JournalTab = 'journal' | 'alertes' | 'runs';

function readTab(rawSearch: unknown): JournalTab {
  const search =
    typeof rawSearch === 'object' && rawSearch !== null
      ? (rawSearch as Record<string, unknown>)
      : {};
  if (search.view === 'alertes') {
    return 'alertes';
  }
  return search.view === 'runs' ? 'runs' : 'journal';
}

const helper = createDataTableColumnHelper<EventListItem>();

const baseColumns = helper.columns([
  helper.accessor('at', {
    id: 'at',
    header: 'Date',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.display({
    id: 'event',
    header: 'Événement',
    cell: ({ row }) => (
      <div className="flex min-w-0 max-w-xl flex-col gap-0.5">
        <span className="font-medium text-foreground">{row.original.titleFr}</span>
        <span className="break-words font-mono text-xs text-muted-foreground">
          {row.original.code}
        </span>
        <span className="text-xs leading-5 text-muted-foreground">{row.original.message}</span>
      </div>
    ),
  }),
  helper.accessor('severity', {
    id: 'severity',
    header: 'Sévérité',
    cell: (info) => <StatusBadge registry={EVENT_SEVERITY} code={info.getValue()} />,
  }),
  helper.accessor('origin', {
    id: 'origin',
    header: 'Origine',
    cell: (info) => {
      const origin = info.getValue();
      return origin ? (
        <OriginBadge code={origin} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  }),
  helper.display({
    id: 'detail',
    header: 'Détail',
    cell: ({ row }) => (
      <JournalDetailCell detail={row.original.detail} subject={row.original.subject} />
    ),
  }),
]);

const alertColumns = helper.columns([
  helper.display({
    id: 'alertEmail',
    header: 'Email',
    cell: ({ row }) => {
      const status = row.original.alert?.email ?? null;
      return status ? (
        <StatusBadge registry={ALERT_DELIVERY_STATUS} code={status} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  }),
  helper.display({
    id: 'alertSlack',
    header: 'Slack',
    cell: ({ row }) => {
      const status = row.original.alert?.slack ?? null;
      return status ? (
        <StatusBadge registry={ALERT_DELIVERY_STATUS} code={status} />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  }),
]);

/**
 * Écran Journal (`02` §11) — trois onglets pilotés par le paramètre d'URL
 * `view` : **Journal** (le quotidien, événements techniques masqués),
 * **Alertes** (les problèmes, avec leur envoi email/Slack) et **Runs**
 * (Suivi Martelet + Pennylane, avec les boutons de lancement).
 */
export function JournalPage(): JSX.Element {
  const rawSearch: unknown = useSearch({ strict: false });
  const navigate = useNavigate();
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const { origin, setOrigin } = useOriginFilter();
  const filters = readJournalFilters(rawSearch);
  const tab = readTab(rawSearch);
  const view: EventView = tab === 'alertes' ? 'alertes' : 'journal';

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    sortDir,
    view,
    ...(sortBy ? { sortBy } : {}),
    ...(filters.technical ? { technical: true } : {}),
    ...(filters.severity.length > 0 ? { severity: filters.severity } : {}),
    ...(filters.code.length > 0 ? { code: filters.code } : {}),
    ...(origin ? { origin: [origin] } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.journal.list(listParams),
    queryFn: () => fetchJournalList(listParams),
    enabled: tab !== 'runs',
  });

  function setTab(next: JournalTab): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, view: next, page: 1 }),
      replace: true,
    });
  }

  const columns = tab === 'alertes' ? [...baseColumns, ...alertColumns] : baseColumns;

  return (
    <Page>
      <PageHeader
        title="Journal"
        description="Ce qui s’est passé au quotidien, les problèmes à traiter, et l’historique des runs."
      />

      <Tabs
        value={tab}
        onValueChange={(next) => {
          setTab(next === 'alertes' ? 'alertes' : next === 'runs' ? 'runs' : 'journal');
        }}
      >
        <TabsList>
          <TabsTrigger value="journal">Journal</TabsTrigger>
          <TabsTrigger value="alertes">Alertes</TabsTrigger>
          <TabsTrigger value="runs">Runs</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'runs' ? (
        <RunsTable />
      ) : (
        <DataTable
          columns={columns}
          data={query.data?.items ?? []}
          getRowId={(row) => row.id}
          total={query.data?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          sortBy={sortBy}
          sortDir={sortDir}
          sortableColumnIds={SORTABLE_COLUMNS}
          onPageChange={setPage}
          onSortChange={setSort}
          isLoading={query.isPending}
          isError={query.isError}
          {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
          errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => {
            void query.refetch();
          }}
          toolbar={
            <ListToolbar
              search={{
                value: filters.search ?? '',
                onChange: (next) => {
                  setFilters({ search: next === '' ? undefined : next });
                },
                placeholder: 'Message, événement, détail…',
              }}
              isFiltered={hasActiveJournalFilters(filters, origin)}
              onReset={() => {
                setOrigin(null);
                setFilters({
                  severity: undefined,
                  code: undefined,
                  technical: undefined,
                  from: undefined,
                  to: undefined,
                });
              }}
            >
              <JournalFilters
                value={filters}
                onChange={setFilters}
                origin={origin}
                onOriginChange={setOrigin}
                view={view}
              />
            </ListToolbar>
          }
          emptyState={
            tab === 'alertes'
              ? {
                  title: 'Aucune alerte',
                  description: 'Aucun avertissement ni erreur ne correspond à ces filtres.',
                }
              : {
                  title: 'Aucun événement',
                  description: 'Aucun événement ne correspond à ces filtres.',
                }
          }
        />
      )}
    </Page>
  );
}
